import { ReactNode } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import type { SxProps, Theme } from '@mui/material/styles';
import { i18n } from 'utils';

interface CloseButtonProps {
  /** Acción de cierre. Se invoca sin argumentos (no recibe el evento). */
  onClose: () => void;
  /** Deshabilita la X mientras hay una operación en curso (guardado, envío...). */
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

const asArray = (sx?: SxProps<Theme>) => (Array.isArray(sx) ? sx : [sx]);

/**
 * Botón "X" de cierre de modal.
 *
 * Normalmente no se usa directo: `ModalTitle` ya lo coloca. Se exporta suelto para
 * las cabeceras que no son un `DialogTitle` (AppBar + Toolbar de Ajustes / Cambio de
 * contraseña, cabeceras con styled-components) y para el `Modal` de gráficas, donde
 * la X flota sobre el contenido porque no hay título.
 */
export const ModalCloseButton = ({ onClose, disabled, sx }: CloseButtonProps) => (
  <IconButton aria-label={i18n('common.dialog.button.close')} onClick={() => onClose()} disabled={disabled} sx={sx}>
    <CloseIcon />
  </IconButton>
);

interface Props extends Partial<CloseButtonProps> {
  /** Título del modal (ya traducido). */
  children: ReactNode;
  /** Estilos extra para el `DialogTitle`. */
  sx?: SxProps<Theme>;
}

/**
 * Cabecera estándar de un modal: `DialogTitle` con la "X" de cierre arriba a la derecha.
 *
 * Antes cada modal resolvía el cierre a su manera —la mayoría solo con el botón
 * "Cerrar" del pie, alguno con un `<CloseIcon>` suelto sin área de pulsación ni
 * `aria-label`— así que la X aparecía o no según la pantalla. Aquí la X es siempre
 * el mismo `IconButton`, en la misma esquina, y ejecuta lo mismo que el botón
 * "Cerrar"/"Cancelar" del pie.
 *
 * Sin `onClose` se comporta como un `DialogTitle` normal: los diálogos de
 * confirmación (borrar, archivar, cerrar sesión) no llevan X a propósito, para
 * obligar a elegir entre aceptar y cancelar.
 *
 * @example
 * <ModalTitle onClose={handleClose}>{i18n('group.detail.resolutions.add')}</ModalTitle>
 */
const ModalTitle = ({ children, onClose, disabled, sx }: Props) => (
  <DialogTitle sx={[{ pr: onClose ? 6 : undefined }, ...asArray(sx)]}>
    {children}
    {onClose && (
      <ModalCloseButton
        onClose={onClose}
        disabled={disabled}
        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      />
    )}
  </DialogTitle>
);

export default ModalTitle;
